import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Check, Globe, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { mockDataIndia } from '../mockIndia';
import { usePageTracking, trackFormSubmit, trackSignupStart } from '../hooks/useAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IndiaPage = () => {
  const location = useLocation();
  
  // Track page view
  usePageTracking('/hindi');
  
  const [language, setLanguage] = useState('en'); // 'en' for English, 'hi' for Hindi
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    agreedToPrivacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [signupFormData, setSignupFormData] = useState({
    name: '',
    email: '',
    phone: '',
    agreedToPrivacy: false
  });

  // Get content based on selected language
  const content = mockDataIndia[language];

  // Scroll to form if navigated from Start page
  useEffect(() => {
    if (location.state?.scrollToForm) {
      setTimeout(() => {
        document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'hi' : 'en');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, agreedToPrivacy: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreedToPrivacy) {
      toast.error(language === 'en' ? 'You must accept the privacy policy.' : 'आपको गोपनीयता नीति स्वीकार करनी होगी।');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contacts/`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        agreed_to_privacy: formData.agreedToPrivacy,
        source: 'india'
      });

      toast.success(language === 'en' 
        ? 'Thank you! Form submitted successfully. We will contact you soon.' 
        : 'धन्यवाद! फॉर्म सफलतापूर्वक सबमिट हो गया। हम जल्द ही आपसे संपर्क करेंगे।');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        agreedToPrivacy: false
      });
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error.response?.data?.detail || (language === 'en' ? 'An error occurred. Please try again.' : 'एक त्रुटि हुई। कृपया पुनः प्रयास करें।');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBookCall = () => {
    window.open('https://calendly.com/electrumprosperitygroup/epg-opportunity-call-india', '_blank');
  };

  const handleJoinNow = () => {
    setShowSignupModal(true);
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    
    if (!signupFormData.agreedToPrivacy) {
      toast.error(language === 'en' ? 'You must accept the privacy policy.' : 'आपको गोपनीयता नीति स्वीकार करनी होगी।');
      return;
    }

    if (!signupFormData.name || !signupFormData.email) {
      toast.error(language === 'en' ? 'Please fill in your name and email.' : 'कृपया अपना नाम और ईमेल भरें।');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contacts/`, {
        name: signupFormData.name,
        email: signupFormData.email,
        phone: signupFormData.phone,
        agreed_to_privacy: signupFormData.agreedToPrivacy,
        source: 'india-signup'
      });

      const response = await axios.get(`${API}/join-links/next-rotation`);
      const joinLink = response.data.join_now_link;
      
      toast.success(language === 'en' 
        ? 'Thank you! You will be redirected to the signup page.' 
        : 'धन्यवाद! आपको साइनअप पेज पर रीडायरेक्ट किया जाएगा।');
      
      setShowSignupModal(false);
      setSignupFormData({
        name: '',
        email: '',
        phone: '',
        agreedToPrivacy: false
      });

      setTimeout(() => {
        window.open(joinLink, '_blank');
      }, 500);

    } catch (error) {
      console.error('Signup form error:', error);
      toast.error(language === 'en' ? 'An error occurred. Please try again.' : 'एक त्रुटि हुई। कृपया पुनः प्रयास करें।');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <img src="/epg_logo.png" alt="EPG Logo" className="logo-icon" />
            <span className="logo-text">{content.header.logoText}</span>
          </div>
          <div className="header-buttons" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* Language Toggle */}
            <Button 
              variant="outline" 
              className="header-btn" 
              onClick={toggleLanguage}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                background: 'rgba(212, 175, 55, 0.1)',
                borderColor: '#D4AF37',
              }}
            >
              <Globe size={16} />
              {language === 'en' ? 'हिंदी' : 'English'}
            </Button>
            <Button variant="outline" className="header-btn" onClick={handleBookCall}>
              {content.header.buttons[0].text}
            </Button>
            <Button variant="outline" className="header-btn" onClick={() => window.open('/privacy', '_blank')}>
              {content.header.buttons[1].text}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">{content.hero.badge}</div>
            <h1 className="hero-headline">{content.hero.headline}</h1>
            <p className="hero-subheading">{content.hero.subheading}</p>
            
            <div className="benefits-list">
              {content.hero.benefits.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <Check className="check-icon" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <div className="stats-grid">
              {content.hero.stats.map((stat, index) => (
                <Card key={index} className="stat-card">
                  <CardContent className="stat-content">
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{stat.label}</div>
                    {stat.sublabel && <div className="stat-sublabel">{stat.sublabel}</div>}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="hero-ctas">
              <Button className="cta-primary" onClick={handleBookCall}>
                {content.hero.ctas[0].text}
              </Button>
              <Button className="cta-secondary" onClick={handleJoinNow}>
                {content.hero.ctas[1].text}
              </Button>
            </div>

            <div className="hero-email">
              Email: <a href={`mailto:${content.hero.email}`}>{content.hero.email}</a>
            </div>
          </div>

          <div className="hero-video">
            <div className="video-placeholder clickable-video">
              <img 
                src="/epg_logo.png" 
                alt="EPG Logo" 
                className="video-logo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Videos Section */}
      <section className="main-videos-section">
        <div className="main-videos-container">
          <div className="video-item">
            <div className="video-player-large clickable-video">
              <img 
                src="/epg_logo.png" 
                alt="EPG Logo" 
                className="video-logo"
              />
            </div>
            <p className="video-title-large">{language === 'en' ? 'Wealth Growth Strategies' : 'धन वृद्धि रणनीतियाँ'}</p>
          </div>
          <div className="video-item">
            <div className="video-player-large clickable-video">
              <img 
                src="/epg_logo.png" 
                alt="EPG Logo" 
                className="video-logo"
              />
            </div>
            <p className="video-title-large">{language === 'en' ? 'Accelerating Wealth Growth' : 'धन वृद्धि को तेज करना'}</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="features-container">
          {content.features.map((feature, index) => (
            <Card key={index} className="feature-card">
              <CardContent className="feature-content">
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
                {index === 2 && (
                  <Button className="feature-btn" onClick={handleBookCall}>
                    {content.header.buttons[0].text}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-section" id="contact-form">
        <div className="contact-container">
          <h2 className="contact-title">{content.contactForm.title}</h2>
          <form onSubmit={handleSubmit} className="contact-form">
            <Input
              type="text"
              name="name"
              placeholder={content.contactForm.fields[0].placeholder}
              value={formData.name}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Input
              type="email"
              name="email"
              placeholder={content.contactForm.fields[1].placeholder}
              value={formData.email}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Input
              type="tel"
              name="phone"
              placeholder={content.contactForm.fields[2].placeholder}
              value={formData.phone}
              onChange={handleInputChange}
              required
              className="form-input"
              disabled={isSubmitting}
            />
            <Button type="submit" className="form-submit" disabled={isSubmitting}>
              {isSubmitting ? (language === 'en' ? 'Sending...' : 'भेजा जा रहा है...') : content.contactForm.submitText}
            </Button>
            <div className="privacy-checkbox">
              <Checkbox
                id="privacy"
                checked={formData.agreedToPrivacy}
                onCheckedChange={handleCheckboxChange}
                disabled={isSubmitting}
              />
              <label htmlFor="privacy" className="privacy-label">
                {content.contactForm.privacyText}{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="privacy-link">{content.contactForm.privacyLink}</a>.
              </label>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-cta">
            <span>{content.footer.cta}</span>
            <Button className="footer-btn" onClick={handleBookCall}>
              {content.header.buttons[0].text}
            </Button>
            <Button className="footer-btn-secondary" onClick={handleJoinNow}>
              {content.footer.ctaButton}
            </Button>
          </div>
          <div className="footer-info">
            <span>{content.footer.copyright}</span>
            <span>•</span>
            <a href={`mailto:${content.footer.email}`}>{content.footer.email}</a>
          </div>
        </div>
      </footer>

      {/* Signup Modal */}
      {showSignupModal && (
        <div className="signup-modal-overlay" onClick={() => setShowSignupModal(false)}>
          <div className="signup-modal" onClick={(e) => e.stopPropagation()}>
            <button className="signup-modal-close" onClick={() => setShowSignupModal(false)}>
              <X size={24} />
            </button>
            <h2 className="signup-modal-title">{language === 'en' ? 'Sign Up' : 'साइन अप'}</h2>
            <p className="signup-modal-subtitle">
              {language === 'en' 
                ? 'Complete the form to continue to the signup page' 
                : 'साइनअप पेज पर जाने के लिए फॉर्म भरें'}
            </p>
            
            <form onSubmit={handleSignupSubmit} className="signup-modal-form">
              <div className="signup-form-field">
                <Input
                  type="text"
                  id="signup_name_in"
                  placeholder={language === 'en' ? 'Full Name *' : 'पूरा नाम *'}
                  value={signupFormData.name}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="signup-input"
                />
              </div>
              <div className="signup-form-field">
                <Input
                  type="email"
                  id="signup_email_in"
                  placeholder={language === 'en' ? 'Email *' : 'ईमेल *'}
                  value={signupFormData.email}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  className="signup-input"
                />
              </div>
              <div className="signup-form-field">
                <Input
                  type="tel"
                  id="signup_phone_in"
                  placeholder={language === 'en' ? 'Phone' : 'फोन'}
                  value={signupFormData.phone}
                  onChange={(e) => setSignupFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="signup-input"
                />
              </div>
              <div className="signup-checkbox-container">
                <Checkbox
                  id="signup-privacy-in"
                  checked={signupFormData.agreedToPrivacy}
                  onCheckedChange={(checked) => setSignupFormData(prev => ({ ...prev, agreedToPrivacy: checked }))}
                />
                <label htmlFor="signup-privacy-in" className="signup-checkbox-label">
                  {language === 'en' 
                    ? <>I accept the <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></>
                    : <>मैं <a href="/privacy" target="_blank" rel="noopener noreferrer">गोपनीयता नीति</a> स्वीकार करता/करती हूं</>}
                </label>
              </div>
              <Button 
                type="submit" 
                className="signup-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting 
                  ? (language === 'en' ? 'Processing...' : 'प्रोसेसिंग...') 
                  : (language === 'en' ? 'Next' : 'आगे')}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndiaPage;
